import {
  getResourceOverview,
  getResourceUsageTrend,
  getResourceQuotaUsageTrend,
  getResourceQuotas,
  allocateResourceQuota,
  updateResourceQuota,
  deleteResourceQuota,
  batchDeleteResourceQuotas,
  getResourceQuotaDetail,
  getResourceUsageDetails,
  getQuotaConfig,
  updateQuotaConfig,
  generateResourceReport,
  exportResourceReport,
  exportResourceQuotas,
  updateResourceQuotaStatus,
  batchUpdateResourceQuotas,
  previewBatchUpdateQuotas,
  executeBatchUpdateQuotas,
  getQuotaTransactions,
  getDetailedStats
} from '@/apis/resource/resource.js'

import { getAlarmHistory as getAlerts, resolveAlarm as handleAlert } from '@/apis/alarm/alarm.js'

const silentError = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[Resource]', ...args.map(a => a?.msg || a?.message || a))
  }
}

const state = {
  // 资源概览数据
  resourceOverview: {
    total: 0,
    normal: 0,
    warning: 0,
    abnormal: 0
  },
  
  // 当前配额详情
  currentQuotaDetail: null,
  
  // 资源使用趋势数据
  resourceUsageTrend: {
    data: [],
    loading: false
  },
  
  // 资源配额列表
  resourceQuotas: {
    list: [],
    total: 0,
    loading: false,
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0
    }
  },
  
  // 告警列表
  alerts: {
    list: [],
    total: 0,
    loading: false,
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0
    }
  },
  
  // 配额配置
  quotaConfig: {
    thresholds: {
      utilization_warning: 70,
      utilization_critical: 85,
      sudden_increase: 50
    },
    units: {
      compute: '小时',
      storage: 'GB',
      network: 'GB',
      memory: 'MB'
    },
    loading: false
  },
  
  // 资源使用明细
  usageDetails: {
    list: [],
    total: 0,
    loading: false,
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0
    }
  }
}

const mutations = {
  // 设置资源概览
  SET_RESOURCE_OVERVIEW(state, overview) {
    state.resourceOverview = overview
  },
  
  // 设置当前配额详情
  SET_CURRENT_QUOTA_DETAIL(state, detail) {
    state.currentQuotaDetail = detail
  },
  
  // 设置资源使用趋势数据
  SET_RESOURCE_USAGE_TREND(state, { data, loading = false }) {
    state.resourceUsageTrend.data = data
    state.resourceUsageTrend.loading = loading
  },
  
  // 设置资源配额列表
  SET_RESOURCE_QUOTAS(state, { list, total, loading = false }) {
    state.resourceQuotas.list = list
    state.resourceQuotas.total = total
    state.resourceQuotas.loading = loading
  },
  
  // 设置资源配额分页
  SET_RESOURCE_QUOTAS_PAGINATION(state, pagination) {
    state.resourceQuotas.pagination = { ...state.resourceQuotas.pagination, ...pagination }
  },
  
  // 添加资源配额
  ADD_RESOURCE_QUOTA(state, quota) {
    state.resourceQuotas.list.unshift(quota)
    state.resourceQuotas.total += 1
  },
  
  // 更新资源配额
  UPDATE_RESOURCE_QUOTA(state, updatedQuota) {
    if (!updatedQuota || !updatedQuota.id) return
    const index = state.resourceQuotas.list.findIndex(item => item.id === updatedQuota.id)
    if (index !== -1) {
      // 局部更新，避免丢失其他字段
      const currentItem = state.resourceQuotas.list[index]
      state.resourceQuotas.list.splice(index, 1, { ...currentItem, ...updatedQuota })
    }
  },
  
  // 删除资源配额
  DELETE_RESOURCE_QUOTA(state, quotaId) {
    state.resourceQuotas.list = state.resourceQuotas.list.filter(item => item.id !== quotaId)
    state.resourceQuotas.total -= 1
  },
  
  // 批量删除资源配额
  BATCH_DELETE_RESOURCE_QUOTAS(state, quotaIds) {
    state.resourceQuotas.list = state.resourceQuotas.list.filter(item => !quotaIds.includes(item.id))
    state.resourceQuotas.total -= quotaIds.length
  },
  
  // 设置告警列表
  SET_ALERTS(state, { list, total, loading = false }) {
    state.alerts.list = list
    state.alerts.total = total
    state.alerts.loading = loading
  },
  
  // 设置告警分页
  SET_ALERTS_PAGINATION(state, pagination) {
    state.alerts.pagination = { ...state.alerts.pagination, ...pagination }
  },
  
  // 更新告警状态
  UPDATE_ALERT_STATUS(state, { alertId, status }) {
    const alert = state.alerts.list.find(item => item.id === alertId)
    if (alert) {
      alert.status = status
    }
  },
  
  // 设置配额配置
  SET_QUOTA_CONFIG(state, config) {
    state.quotaConfig = { ...state.quotaConfig, ...config }
  },
  
  // 设置资源使用明细
  SET_USAGE_DETAILS(state, { list, total, loading = false }) {
    state.usageDetails.list = list
    state.usageDetails.total = total
    state.usageDetails.loading = loading
  },
  
  // 设置资源使用明细分页
  SET_USAGE_DETAILS_PAGINATION(state, pagination) {
    state.usageDetails.pagination = { ...state.usageDetails.pagination, ...pagination }
  }
}

const actions = {
  // 获取资源概览数据
  async fetchResourceOverview({ commit }) {
    try {
      const data = await getResourceOverview()
      commit('SET_RESOURCE_OVERVIEW', data)
      return data
    } catch (error) {
      silentError('获取资源概览失败:', error)
      throw error
    }
  },
  
  // 获取资源使用趋势数据
  async fetchResourceUsageTrend({ commit }, params = {}) {
    commit('SET_RESOURCE_USAGE_TREND', { data: [], loading: true })
    try {
      // 兼容字符串 timeRange 或对象 { timeRange, modelId, userId, resourceType }
      const queryParams = typeof params === 'string' ? { timeRange: params } : params
      const data = await getResourceUsageTrend(queryParams)
      commit('SET_RESOURCE_USAGE_TREND', { data, loading: false })
      return data
    } catch (error) {
      silentError('获取资源使用趋势失败:', error)
      commit('SET_RESOURCE_USAGE_TREND', { data: [], loading: false })
      throw error
    }
  },

  // 获取指定配额的使用趋势数据
  async fetchResourceQuotaUsageTrend({ commit }, { id, params }) {
    try {
      const response = await getResourceQuotaUsageTrend(id, params)
      return response
    } catch (error) {
      silentError('获取配额使用趋势失败:', error)
      throw error
    }
  },

  // 获取资源配额列表
  async fetchResourceQuotas({ commit }, params = {}) {
    commit('SET_RESOURCE_QUOTAS', { list: [], total: 0, loading: true })
    try {
      const data = await getResourceQuotas(params)
      commit('SET_RESOURCE_QUOTAS', { 
        list: data.list, 
        total: data.total, 
        loading: false 
      })
      if (params.page !== undefined) {
        commit('SET_RESOURCE_QUOTAS_PAGINATION', { 
          page: params.page, 
          pageSize: params.pageSize || 10,
          total: data.total
        })
      }
      return data
    } catch (error) {
      silentError('获取资源配额列表失败:', error)
      commit('SET_RESOURCE_QUOTAS', { list: [], total: 0, loading: false })
      throw error
    }
  },
  
  // 分配资源配额
  async allocateResourceQuota({ commit }, quotaData) {
    try {
      const data = await allocateResourceQuota(quotaData)
      commit('ADD_RESOURCE_QUOTA', data)
      return data
    } catch (error) {
      silentError('分配资源配额失败:', error)
      throw error
    }
  },
  
  // 更新资源配额
  async updateResourceQuota({ commit }, { id, quotaData }) {
    try {
      const data = await updateResourceQuota(id, quotaData)
      commit('UPDATE_RESOURCE_QUOTA', data)
      return data
    } catch (error) {
      silentError('更新资源配额失败:', error)
      throw error
    }
  },
  
  // 删除资源配额
  async deleteResourceQuota({ commit }, quotaId) {
    try {
      await deleteResourceQuota(quotaId)
      commit('DELETE_RESOURCE_QUOTA', quotaId)
      return quotaId
    } catch (error) {
      silentError('删除资源配额失败:', error)
      throw error
    }
  },
  
  // 批量删除资源配额
  async batchDeleteResourceQuotas({ commit }, quotaIds) {
    try {
      await batchDeleteResourceQuotas(quotaIds)
      commit('BATCH_DELETE_RESOURCE_QUOTAS', quotaIds)
      return quotaIds
    } catch (error) {
      silentError('批量删除资源配额失败:', error)
      throw error
    }
  },
  
  // 获取资源配额详情
  async fetchResourceQuotaDetail({ commit }, quotaId) {
    console.log(`[Resource Store] fetchResourceQuotaDetail start for ID: ${quotaId}`)
    commit('SET_CURRENT_QUOTA_DETAIL', null) // 开始获取前清空
    try {
      if (!quotaId) {
        console.warn('[Resource Store] fetchResourceQuotaDetail: quotaId is missing')
        return null
      }
      const data = await getResourceQuotaDetail(quotaId)
      console.log(`[Resource Store] fetchResourceQuotaDetail success:`, data)
      commit('SET_CURRENT_QUOTA_DETAIL', data)
      return data
    } catch (error) {
      silentError(`[Resource Store] fetchResourceQuotaDetail error for ID ${quotaId}:`, error)
      // 如果是 404 或者其他错误，返回 null 而不是直接抛出，让组件层决定如何处理
      if (error.response && error.response.status === 404) {
        return null
      }
      throw error
    }
  },
  
  // 获取告警列表
  async fetchAlerts({ commit }, params = {}) {
    commit('SET_ALERTS', { list: [], total: 0, loading: true })
    try {
      const data = await getAlerts(params)
      commit('SET_ALERTS', { 
        list: data.list, 
        total: data.total, 
        loading: false 
      })
      if (params.page !== undefined) {
        commit('SET_ALERTS_PAGINATION', { 
          page: params.page, 
          pageSize: params.limit || 10,
          total: data.total
        })
      }
      return data
    } catch (error) {
      silentError('获取告警列表失败:', error)
      commit('SET_ALERTS', { list: [], total: 0, loading: false })
      throw error
    }
  },
  
  // 处理告警
  async processAlert({ commit }, { alertId, status = 'processed' }) {
    try {
      const data = await handleAlert(alertId, { status })
      commit('UPDATE_ALERT_STATUS', { alertId, status })
      return data
    } catch (error) {
      silentError('处理告警失败:', error)
      throw error
    }
  },
  
  // 获取配额配置
  async fetchQuotaConfig({ commit }) {
    commit('SET_QUOTA_CONFIG', { loading: true })
    try {
      const data = await getQuotaConfig()
      commit('SET_QUOTA_CONFIG', { ...data, loading: false })
      return data
    } catch (error) {
      silentError('获取配额配置失败:', error)
      commit('SET_QUOTA_CONFIG', { loading: false })
      throw error
    }
  },
  
  // 更新配额配置
  async updateQuotaConfig({ commit }, config) {
    try {
      const data = await updateQuotaConfig(config)
      commit('SET_QUOTA_CONFIG', config)
      return data
    } catch (error) {
      silentError('更新配额配置失败:', error)
      throw error
    }
  },
  
  // 获取资源使用明细
  async fetchUsageDetails({ commit }, params = {}) {
    commit('SET_USAGE_DETAILS', { list: [], total: 0, loading: true })
    try {
      const data = await getResourceUsageDetails(params)
      commit('SET_USAGE_DETAILS', { 
        list: data.list, 
        total: data.total, 
        loading: false 
      })
      if (params.page !== undefined) {
        commit('SET_USAGE_DETAILS_PAGINATION', { 
          page: params.page, 
          pageSize: params.pageSize || 10,
          total: data.total
        })
      }
      return data
    } catch (error) {
      silentError('获取资源使用明细失败:', error)
      commit('SET_USAGE_DETAILS', { list: [], total: 0, loading: false })
      throw error
    }
  },
  
  // 检查资源使用异常（突增50%以上）
  checkResourceAnomaly({ dispatch, state }, { resourceType, currentUsage, previousUsage }) {
    const threshold = state.quotaConfig.thresholds.sudden_increase
    if (previousUsage > 0) {
      const increaseRate = ((currentUsage - previousUsage) / previousUsage) * 100
      if (increaseRate >= threshold) {
        // 触发异常告警
        return dispatch('triggerAnomalyAlert', {
          resourceType,
          currentUsage,
          previousUsage,
          increaseRate
        })
      }
    }
    return Promise.resolve()
  },
  
  // 触发异常告警
  async triggerAnomalyAlert({ dispatch }, { resourceType, currentUsage, previousUsage, increaseRate }) {
    try {
      // 这里应该调用创建告警的API
      console.log(`资源异常告警: ${resourceType} 使用量突增 ${increaseRate.toFixed(1)}%`)
      
      // 刷新告警列表
      await dispatch('fetchAlerts')
      
      return { success: true }
    } catch (error) {
      silentError('触发异常告警失败:', error)
      throw error
    }
  },
  
  // 生成资源报表
  async generateResourceReport({ commit }, params) {
    try {
      const data = await generateResourceReport(params)
      return data
    } catch (error) {
      silentError('生成资源报表失败:', error)
      throw error
    }
  },
  
  // 导出资源报表
  async exportResourceReport({ commit }, params) {
    try {
      const blob = await exportResourceReport(params)
      return blob
    } catch (error) {
      silentError('导出资源报表失败:', error)
      throw error
    }
  },
  
  // 导出资源配额列表
  async exportResourceQuotas({ commit }, params) {
    try {
      const response = await exportResourceQuotas(params)
      return response
    } catch (error) {
      silentError('导出资源配额列表失败:', error)
      throw error
    }
  },

  // 更新资源配额状态
  async updateResourceQuotaStatus({ commit }, { id, status }) {
    try {
      const data = await updateResourceQuotaStatus(id, status)
      // 如果后端返回为空（如 204 No Content），构造一个包含 id 和新状态的对象进行更新
      const updateData = data || { id, status }
      commit('UPDATE_RESOURCE_QUOTA', updateData)
      return updateData
    } catch (error) {
      silentError('更新资源配额状态失败:', error)
      throw error
    }
  },

  // 批量更新资源配额
  async batchUpdateResourceQuotas({ commit, dispatch }, { ids, dto }) {
    try {
      await batchUpdateResourceQuotas({ ids, dto })
      // 批量更新后刷新列表
      return dispatch('fetchResourceQuotas')
    } catch (error) {
      silentError('批量更新资源配额失败:', error)
      throw error
    }
  },

  // 预览批量调整配额
  async previewBatchUpdateQuotas({ commit }, data) {
    try {
      const response = await previewBatchUpdateQuotas(data)
      return response
    } catch (error) {
      silentError('预览批量调整配额失败:', error)
      throw error
    }
  },

  // 执行批量调整配额
  async executeBatchUpdateQuotas({ commit, dispatch }, data) {
    try {
      const response = await executeBatchUpdateQuotas(data)
      // 批量调整后刷新列表
      dispatch('fetchResourceQuotas')
      return response
    } catch (error) {
      silentError('执行批量调整配额失败:', error)
      throw error
    }
  },

  // 获取详细统计数据 (模型使用比例、用户排行等)
  async fetchDetailedStats({ commit }, params = {}) {
    try {
      const data = await getDetailedStats(params)
      return data
    } catch (error) {
      silentError('获取详细统计数据失败:', error)
      throw error
    }
  }
}

const getters = {
  // 资源概览
  resourceOverview: state => state.resourceOverview,
  
  // 资源使用趋势
  resourceUsageTrend: state => state.resourceUsageTrend.data,
  resourceUsageTrendLoading: state => state.resourceUsageTrend.loading,
  
  // 资源配额
  resourceQuotas: state => state.resourceQuotas.list,
  resourceQuotasTotal: state => state.resourceQuotas.total,
  resourceQuotasLoading: state => state.resourceQuotas.loading,
  resourceQuotasPagination: state => state.resourceQuotas.pagination,
  
  // 告警
  alerts: state => state.alerts.list,
  alertsTotal: state => state.alerts.total,
  alertsLoading: state => state.alerts.loading,
  alertsPagination: state => state.alerts.pagination,
  unprocessedAlerts: state => state.alerts.list.filter(alert => alert.status === 'unprocessed'),
  processedAlerts: state => state.alerts.list.filter(alert => alert.status === 'processed'),
  
  // 配额配置
  quotaConfig: state => state.quotaConfig,
  quotaConfigLoading: state => state.quotaConfig.loading,
  quotaThresholds: state => state.quotaConfig.thresholds,
  resourceUnits: state => state.quotaConfig.units,
  
  // 资源使用明细
  usageDetails: state => state.usageDetails.list,
  usageDetailsTotal: state => state.usageDetails.total,
  usageDetailsLoading: state => state.usageDetails.loading,
  usageDetailsPagination: state => state.usageDetails.pagination
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
